import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { context } from '@adornis/baseql/server/context.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer, loadRawEntityDataByID } from '@adornis/bh-util/util.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { getDbPluginForScope } from '../db-plugins.js';
import { LECTURER_PERMISSIONS, Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { Participation } from './Participation.js';
import { readEntitiesByIDs } from '../server/entity-reads.js';

// create indices
export async function ensureBHPaymentIndexes(): Promise<void> {
  const paymentCollection = mongo().collection('payments');
  await paymentCollection.createIndex({ 'userReference.userID': 1 });
  // getParticipationCurrentPaymentServer does find({ participationIDs }).sort({ createdAt: -1 }), and
  // `currentPayment` is part of the Participation @Materialize selectionSet — so EVERY materialized
  // rebuild (plus every payment-status resolution and the dataview rows) ran a full COLLSCAN of the
  // ever-growing payments collection. A burst of participation rebuilds (suspend/move) meant N payment
  // COLLSCANs. This multikey index turns each into a point lookup.
  await paymentCollection.createIndex({ participationIDs: 1 });
}

@Entity()
export class BHOrderNr extends AdornisEntity {
  static override _class = 'BHOrderNr';

  @validate(nonOptional())
  @Field(type => Int)
  year!: number;

  @validate(nonOptional())
  @Field(type => Int)
  nr!: number;

  static async create(institutionID: string, ...other: any) {
    const year = new Date().getFullYear();
    const { getIncrementingNumber } = await import('@adornis/counter/api/counter.operations.js');
    const nr = await getIncrementingNumber(institutionID + '::orderCounter::' + year)();
    return new BHOrderNr({ year, nr });
  }

  getOrderNr() {
    const padNr = this.nr.toString().padStart(6, '0');
    return `${this.year}/${padNr}`;
  }
}

@Entity()
export class BHPayment extends Payment {
  static override _class = 'BHPayment';
  static override _collectionName = 'payments';

  @validate(nonOptional())
  @Field(type => [String])
  participationIDs!: string[];

  @DataLoadedField(type => [Participation], ({ participationIDs }) => participationIDs)
  participations!: Participation[];

  @Field(type => BHOrderNr)
  orderNr!: BHOrderNr;

  // Note: currently only used for sd-plugin-invoices
  @Field(type => Int)
  discount?: number;

  static override async beforeCreate(entity: BHPayment) {
    const participationID = entity.participationIDs[0];
    if (!participationID) throw new Error('no participations available');
    const user = await getCurrentUserServer();
    if (!context.serverContext && !user) throw new Error('you are not authenticated');
    const participationFields = { _id: 1, productID: 1 } as const;
    // The legacy participation operation checks access explicitly, independently of publish filters.
    const [participation] = await runInServerContext(() =>
      readEntitiesByIDs<typeof Participation, typeof participationFields>(
        Participation,
        [participationID],
        participationFields,
      ),
    );
    if (!participation) throw new Error(`No ${Participation._class} found with id ${participationID}`);
    const { Product } = await import('./products.js');
    if (!context.serverContext) {
      if (!user) throw new Error('unreachable');
      if (user._id !== (participation as Participation).userID && !user.isSuperAdmin) {
        const permissionFields = { institutionID: 1 } as const;
        const [permissionProduct] = await runInServerContext(() =>
          readEntitiesByIDs<typeof Product, typeof permissionFields>(
            Product,
            [participation.productID],
            permissionFields,
          ),
        );
        if (!permissionProduct) throw new Error(`No ${Product._class} found with id ${participation.productID}`);
        if (
          !user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, permissionProduct.institutionID) &&
          !user.hasPermission(LECTURER_PERMISSIONS.MANAGE_PARTICIPATION as any, permissionProduct.institutionID)
        )
          throw new Error('You dont have enough permission!');
      }
    }
    const productFields = { _id: 1, institutionID: 1 } as const;
    const [product] = await readEntitiesByIDs<typeof Product, typeof productFields>(
      Product,
      [participation.productID as string],
      productFields,
    );
    if (!product) throw new Error('product not found');
    const institutionID = product.institutionID;
    if (!(entity.orderNr as any)) entity.orderNr = await BHOrderNr.create(institutionID);

    if (!(entity.createdAt as any)) entity.createdAt = new Date();
  }

  static override async afterUpdate(oldPayment: BHPayment, newPayment: BHPayment, _result: any) {
    const [resolvedNewPayment] = await readEntitiesByIDs(
      BHPayment,
      [newPayment._id],
      selectionSet(() => BHPayment, 2, ['statusUpdates']) as any,
    );
    if (!resolvedNewPayment) throw new Error('somthing went wrong');
    const participationID = resolvedNewPayment.participationIDs[0];
    if (!participationID) return;
    const participation = await loadRawEntityDataByID(Participation, participationID, { productID: 1 });
    if (!participation) return;
    const { Product } = await import('./products.js');
    const product = await loadRawEntityDataByID(Product, participation.productID, { institutionID: 1 });
    if (!product) return;
    const institutionID = product.institutionID;
    if (!institutionID) return;
    const paymentPlugin = getDbPluginForScope('payment', institutionID);
    await paymentPlugin.onPaymentUpdate?.(institutionID, oldPayment as any, resolvedNewPayment as any);
  }
}
